import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '../Icon/Icon';
import {
  SettingsCard,
  SettingsCards,
  SettingsFooter,
  SettingsPageHeader,
} from '../SettingsShell/SettingsPage';
import { SetCheck } from '../SettingsShell/controls';
import { INSIGHT_GROUPS } from '../../settings-booking/types';
import type { InsightCheck, InsightGroup, InsightView } from '../../settings-booking/types';

export interface InsightsViewProps {
  checks: InsightCheck[];
  location: string;
  /** Persist after a tick, Select all, or Clear all. Not called for the initial list. */
  onChange?: (checks: InsightCheck[]) => void;
}

const VIEWS: { id: InsightView; n: string; title: string; sub: string }[] = [
  { id: 'daily', n: '01', title: 'Daily view', sub: 'What a seasoned colleague would flag before the day starts.' },
  { id: 'weekly', n: '02', title: 'Weekly view', sub: 'What there is still time to act on.' },
  {
    id: 'monthly',
    n: '03',
    title: 'Monthly view',
    sub: 'Where the month landed, and what is due before it closes.',
  },
];

/**
 * Appointment & booking › Calendar insights (spec 15 §10).
 *
 * Checkboxes only, all three views on one page. Not three tabs — the whole page
 * is checkboxes and there is no reason to make somebody click twice to compare.
 * Not one table with three columns either: the three views do not share a check
 * list, so a matrix would be mostly empty cells, and an empty cell reads as a
 * switch somebody forgot to turn on.
 */
export function InsightsView({ checks: initial, location, onChange }: InsightsViewProps) {
  const [checks, setChecks] = useState(initial);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    onChangeRef.current?.(checks);
  }, [checks]);

  const forView = (v: InsightView) => checks.filter((c) => c.view === v);
  const onIn = (list: InsightCheck[]) => list.filter((c) => c.enabled).length;

  const setView = (v: InsightView, enabled: boolean) => {
    setChecks((prev) => prev.map((c) => (c.view === v ? { ...c, enabled } : c)));
  };

  const totalOn = onIn(checks);

  return (
    <div>
      <SettingsPageHeader
        eyebrow="Settings · Appointment & booking"
        titleLead="Calendar"
        titleAccent="insights"
        sub={
          <>
            What ELVA flags in the Insights panel beside the calendar.
          </>
        }
      />

      {/* jump strip — counts derived */}
      <div className="mb-6 flex flex-wrap gap-3">
        {VIEWS.map((v) => {
          const list = forView(v.id);
          return (
            <a
              key={v.id}
              href={`#insights-${v.id}`}
              className="flex items-center gap-2.5 rounded-[12px] border border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-2.5 hover:bg-[var(--surface-sunken)]"
            >
              <span className="text-[14px] font-bold text-[var(--text-primary)]">
                {v.id.charAt(0).toUpperCase() + v.id.slice(1)}
              </span>
              <span className="text-[12.5px] text-[var(--text-muted)]">
                <strong className="font-[var(--font-mono)] text-[var(--text-secondary)]">{onIn(list)}</strong> of{' '}
                {list.length} checks
              </span>
            </a>
          );
        })}
      </div>

      <SettingsCards>
        {VIEWS.map((v) => {
          const list = forView(v.id);
          return (
            <div key={v.id} id={`insights-${v.id}`}>
              <SettingsCard n={v.n} title={v.title} sub={v.sub}>
                <div className="grid gap-4 p-6 md:grid-cols-2">
                  {INSIGHT_GROUPS.map((g) => {
                    const inGroup = list.filter((c) => c.group === g.id);
                    if (inGroup.length === 0) return null;
                    return (
                      <GroupPanel
                        key={g.id}
                        id={g.id}
                        label={g.label}
                        colour={g.colour}
                        checks={inGroup}
                        onToggle={(id, next) => {
                          setChecks((prev) => prev.map((c) => (c.id === id ? { ...c, enabled: next } : c)));
                        }}
                      />
                    );
                  })}
                </div>

                <div className="flex flex-wrap items-center justify-between gap-4 bg-[var(--surface-sunken)] px-6 py-4">
                  <span className="text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
                    <strong className="text-[var(--text-secondary)]">
                      {onIn(list)} of {list.length}
                    </strong>{' '}
                    checks run on the {v.id} view.
                  </span>
                  <span className="flex shrink-0 items-center gap-4">
                    <button
                      type="button"
                      onClick={() => { setView(v.id, true); }}
                      className="text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline"
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={() => { setView(v.id, false); }}
                      className="text-[12.5px] font-semibold text-[var(--text-muted)] hover:underline"
                    >
                      Clear all
                    </button>
                  </span>
                </div>
              </SettingsCard>
            </div>
          );
        })}
      </SettingsCards>

      <SettingsFooter
        location={location}
        left={
          <>
            {totalOn} of {checks.length} checks on ·{' '}
            {VIEWS.map((v, i) => {
              const list = forView(v.id);
              return (
                <React.Fragment key={v.id}>
                  {i > 0 ? ' · ' : ''}
                  {v.id} {onIn(list)} of {list.length}
                </React.Fragment>
              );
            })}
          </>
        }
      />
    </div>
  );
}

function GroupPanel({
  id,
  label,
  colour,
  checks,
  onToggle,
}: {
  id: InsightGroup;
  label: string;
  colour: string;
  checks: InsightCheck[];
  onToggle: (id: string, next: boolean) => void;
}) {
  const on = checks.filter((c) => c.enabled).length;
  return (
    <section className="rounded-[12px] border border-[var(--border-default)] p-4">
      <div className="flex items-center gap-2">
        <span
          className="flex h-6 w-6 items-center justify-center rounded-[7px]"
          style={{ background: `${colour}1A` }}
        >
          <Icon kind={groupIcon(id)} size={12} stroke={2} style={{ color: colour }} />
        </span>
        <span className="flex-1 text-[12.5px] font-bold text-[var(--text-primary)]">{label}</span>
        <span className="font-[var(--font-mono)] text-[11px] text-[var(--text-muted)]">
          {on} of {checks.length}
        </span>
      </div>
      <div className="mt-3 flex flex-col gap-2.5">
        {checks.map((c) => (
          <SetCheck
            key={c.id}
            tone="plain"
            checked={c.enabled}
            onChange={(next) => { onToggle(c.id, next); }}
          >
            <span className={c.enabled ? 'text-[13px] font-semibold' : 'text-[13px]'}>{c.label}</span>
          </SetCheck>
        ))}
      </div>
    </section>
  );
}

function groupIcon(g: InsightGroup) {
  if (g === 'revenue') return 'bars' as const;
  if (g === 'retention') return 'heart' as const;
  if (g === 'clinical') return 'alert' as const;
  return 'dollar' as const;
}
